import { useState, useMemo, useCallback, memo } from 'react';
import { Paper, Stack, Title, Text, Group, Select, Tabs, useMantineTheme } from '@mantine/core';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import type { TooltipProps } from 'recharts';
import type { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import type { CalculationResults, TariffResult, TariffType } from '../../../../types/calculator';

/**
 * Charts Visualization Component Props
 */
interface ChartsVisualizationProps {
  results: CalculationResults;
  baselineTariff?: TariffType;
}

/**
 * Bar Chart Data Point
 */
interface BarChartDataPoint {
  name: string;
  totalCost: number;
  energyCost: number;
  distributionCost: number;
  isBaseline: boolean;
  isCheapest: boolean;
  isMostExpensive: boolean;
}

/**
 * Pie Chart Data Point
 */
interface PieChartDataPoint {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

/**
 * Custom Tooltip for Bar Chart
 */
function BarChartTooltip({ active, payload }: TooltipProps<ValueType, NameType>) {
  if (!active || !payload?.length) {return null;}

  const data = payload[0].payload;

  return (
    <Paper p="sm" shadow="md" withBorder>
      <Stack gap="xs">
        <Text size="sm" fw={700}>
          {data.name}
        </Text>
        <Text size="xs" c="dimmed">
          Koszt energii: {data.energyCost.toFixed(2)} zł
        </Text>
        <Text size="xs" c="dimmed">
          Koszt dystrybucji: {data.distributionCost.toFixed(2)} zł
        </Text>
        <Text size="sm" fw={600} c="blue.7">
          Suma: {data.totalCost.toFixed(2)} zł
        </Text>
      </Stack>
    </Paper>
  );
}

/**
 * Custom Tooltip for Pie Chart
 */
function PieChartTooltip({ active, payload }: TooltipProps<ValueType, NameType>) {
  if (!active || !payload?.length) {return null;}

  const data = payload[0];

  return (
    <Paper p="sm" shadow="md" withBorder>
      <Stack gap="xs">
        <Text size="sm" fw={700}>
          {data.name}
        </Text>
        <Text size="xs" c="dimmed">
          Wartość: {typeof data.value === 'number' ? data.value.toFixed(2) : data.value} zł
        </Text>
        <Text size="sm" fw={600} style={{ color: data.payload.color }}>
          {data.payload.percentage.toFixed(1)}%
        </Text>
      </Stack>
    </Paper>
  );
}

/**
 * Charts Visualization Component (Memoized)
 *
 * Displays interactive charts for tariff comparison:
 * 1. Bar Chart: Total annual costs for all tariffs
 * 2. Pie Chart: Cost breakdown (energy vs distribution) for selected tariff
 *
 * Features:
 * - Responsive design with ResponsiveContainer
 * - Color coding: green (cheapest), red (most expensive), blue (baseline)
 * - Interactive tooltips with detailed breakdowns
 * - Polish language labels
 * - Mantine UI theming
 *
 * Performance optimizations:
 * - React.memo prevents re-renders when props haven't changed
 * - useMemo for expensive calculations (chart data, costs)
 * - useCallback for event handlers
 */
function ChartsVisualizationInner({ results, baselineTariff = 'G11' }: ChartsVisualizationProps) {
  const theme = useMantineTheme();
  const [selectedTariffForPie, setSelectedTariffForPie] = useState<TariffType>(baselineTariff);

  // Memoize tariff mapping (static data)
  // Multi-OSD Support: Includes G12n (PGE) and G12r (Energa)
  const tariffMapping = useMemo<Array<{ code: TariffType; key: keyof CalculationResults }>>(() => [
    { code: 'G11', key: 'g11' },
    { code: 'G12', key: 'g12' },
    { code: 'G12as', key: 'g12as' },
    { code: 'G12w', key: 'g12w' },
    { code: 'G12n', key: 'g12n' },   // PGE-specific: Nocna
    { code: 'G12r', key: 'g12r' },   // Energa-specific: Rozszerzona weekendowa
    { code: 'G13', key: 'g13' },
    { code: 'G13s', key: 'g13s' }
  ], []);

  // Memoize available tariffs extraction
  const availableTariffs = useMemo<Array<{ code: TariffType; result: TariffResult }>>(() => {
    const tariffs: Array<{ code: TariffType; result: TariffResult }> = [];
    tariffMapping.forEach(({ code, key }) => {
      const result = results[key];
      if (result && typeof result === 'object' && 'tariffName' in result) {
        tariffs.push({ code, result });
      }
    });
    return tariffs;
  }, [results, tariffMapping]);

  // Memoize min/max cost calculations
  const { minCost, maxCost } = useMemo(() => {
    const costs = availableTariffs.map(t => t.result.totalAnnualCost);
    return {
      minCost: Math.min(...costs),
      maxCost: Math.max(...costs)
    };
  }, [availableTariffs]);

  // Memoize bar chart data
  const barChartData = useMemo<BarChartDataPoint[]>(() =>
    availableTariffs.map(({ code, result }) => ({
      name: code,
      totalCost: result.totalAnnualCost,
      energyCost: result.energyCost,
      distributionCost: result.distributionCost,
      isBaseline: code === baselineTariff,
      isCheapest: result.totalAnnualCost === minCost,
      isMostExpensive: result.totalAnnualCost === maxCost
    })),
    [availableTariffs, baselineTariff, minCost, maxCost]
  );

  // Memoize selected tariff data
  const selectedTariffData = useMemo(() =>
    availableTariffs.find(t => t.code === selectedTariffForPie)?.result,
    [availableTariffs, selectedTariffForPie]
  );

  // Memoize pie chart data
  const pieChartData = useMemo<PieChartDataPoint[]>(() =>
    selectedTariffData
      ? [
          {
            name: 'Koszt energii',
            value: selectedTariffData.energyCost,
            percentage: (selectedTariffData.energyCost / selectedTariffData.totalAnnualCost) * 100,
            color: theme.colors.blue[6]
          },
          {
            name: 'Koszt dystrybucji',
            value: selectedTariffData.distributionCost,
            percentage: (selectedTariffData.distributionCost / selectedTariffData.totalAnnualCost) * 100,
            color: theme.colors.green[6]
          }
        ]
      : [],
    [selectedTariffData, theme.colors.blue, theme.colors.green]
  );

  // Memoize color function for bars
  const getBarColor = useCallback((entry: BarChartDataPoint) => {
    if (entry.isCheapest) {return theme.colors.green[6];}
    if (entry.isMostExpensive) {return theme.colors.red[6];}
    if (entry.isBaseline) {return theme.colors.blue[6];}
    return theme.colors.gray[5];
  }, [theme.colors.green, theme.colors.red, theme.colors.blue, theme.colors.gray]);

  // Memoize select options
  const tariffSelectOptions = useMemo(() =>
    availableTariffs.map(({ code }) => ({
      value: code,
      label: code
    })),
    [availableTariffs]
  );

  // Memoize onChange handler
  const handleTariffChange = useCallback((value: string | null) => {
    if (value) {
      setSelectedTariffForPie(value as TariffType);
    }
  }, []);

  return (
    <Stack gap="xl">
      <Tabs defaultValue="comparison" variant="pills">
        <Tabs.List mb="md">
          <Tabs.Tab value="comparison">Porównanie kosztów</Tabs.Tab>
          <Tabs.Tab value="breakdown">Struktura kosztów</Tabs.Tab>
        </Tabs.List>

        {/* Tab 1: Bar Chart - Cost Comparison */}
        <Tabs.Panel value="comparison">
          <Paper p="xl" shadow="sm" withBorder>
            <Stack gap="lg">
              <div>
                <Title order={3} mb="xs">
                  Porównanie rocznych kosztów energii
                </Title>
                <Text size="sm" c="dimmed">
                  Wykres słupkowy przedstawia całkowite roczne koszty dla wszystkich taryf
                </Text>
              </div>

              {/* Legend */}
              <Group gap="lg">
                <Group gap="xs">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: theme.colors.green[6],
                      borderRadius: 4
                    }}
                  />
                  <Text size="xs">Najtańsza taryfa</Text>
                </Group>
                <Group gap="xs">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: theme.colors.red[6],
                      borderRadius: 4
                    }}
                  />
                  <Text size="xs">Najdroższa taryfa</Text>
                </Group>
                <Group gap="xs">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: theme.colors.blue[6],
                      borderRadius: 4
                    }}
                  />
                  <Text size="xs">Taryfa bazowa</Text>
                </Group>
                <Group gap="xs">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      backgroundColor: theme.colors.gray[5],
                      borderRadius: 4
                    }}
                  />
                  <Text size="xs">Pozostałe taryfy</Text>
                </Group>
              </Group>

              {/* Bar Chart */}
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={barChartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.gray[3]} />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: theme.colors.gray[7], fontSize: 14 }}
                    angle={0}
                    textAnchor="middle"
                  />
                  <YAxis
                    tick={{ fill: theme.colors.gray[7], fontSize: 12 }}
                    label={{
                      value: 'Koszt roczny (PLN)',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fill: theme.colors.gray[7], fontSize: 14 }
                    }}
                  />
                  <Tooltip content={<BarChartTooltip />} cursor={{ fill: theme.colors.gray[1] }} />
                  <Bar dataKey="totalCost" radius={[8, 8, 0, 0]}>
                    {barChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getBarColor(entry)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Summary */}
              <Paper p="md" bg="gray.0">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" fw={600}>
                      Różnica między najtańszą a najdroższą taryfą:
                    </Text>
                    <Text size="sm" fw={700} c="green.7">
                      {(maxCost - minCost).toFixed(2)} zł rocznie
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                      To około:
                    </Text>
                    <Text size="sm" c="dimmed">
                      {((maxCost - minCost) / 12).toFixed(2)} zł miesięcznie
                    </Text>
                  </Group>
                </Stack>
              </Paper>
            </Stack>
          </Paper>
        </Tabs.Panel>

        {/* Tab 2: Pie Chart - Cost Breakdown */}
        <Tabs.Panel value="breakdown">
          <Paper p="xl" shadow="sm" withBorder>
            <Stack gap="lg">
              <Group justify="space-between" align="flex-start">
                <div>
                  <Title order={3} mb="xs">
                    Struktura kosztów według taryfy
                  </Title>
                  <Text size="sm" c="dimmed">
                    Podział kosztów na energię i dystrybucję dla wybranej taryfy
                  </Text>
                </div>

                {/* Tariff Selector */}
                <Select
                  label="Wybierz taryfę"
                  value={selectedTariffForPie}
                  onChange={handleTariffChange}
                  data={tariffSelectOptions}
                  style={{ minWidth: 150 }}
                />
              </Group>

              {/* Pie Chart */}
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.percentage.toFixed(1)}%`}
                    outerRadius={120}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={800}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieChartTooltip />} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => <Text size="sm">{value}</Text>}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Breakdown Details */}
              {selectedTariffData && (
                <Paper p="md" bg="gray.0">
                  <Stack gap="md">
                    <Title order={5}>Szczegóły kosztów dla taryfy {selectedTariffForPie}</Title>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: theme.colors.blue[6],
                            borderRadius: '50%'
                          }}
                        />
                        <Text size="sm">Koszt energii:</Text>
                      </Group>
                      <Text size="sm" fw={600}>
                        {selectedTariffData.energyCost.toFixed(2)} zł
                        ({pieChartData[0]?.percentage.toFixed(1)}%)
                      </Text>
                    </Group>
                    <Group justify="space-between">
                      <Group gap="xs">
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: theme.colors.green[6],
                            borderRadius: '50%'
                          }}
                        />
                        <Text size="sm">Koszt dystrybucji:</Text>
                      </Group>
                      <Text size="sm" fw={600}>
                        {selectedTariffData.distributionCost.toFixed(2)} zł
                        ({pieChartData[1]?.percentage.toFixed(1)}%)
                      </Text>
                    </Group>
                    <Group justify="space-between" pt="xs" style={{ borderTop: `1px solid ${theme.colors.gray[3]}` }}>
                      <Text size="sm" fw={700}>
                        Koszt całkowity:
                      </Text>
                      <Text size="sm" fw={700} c="blue.7">
                        {selectedTariffData.totalAnnualCost.toFixed(2)} zł rocznie
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed" fs="italic">
                      Średnia cena za kWh: {selectedTariffData.averageCostPerKwh.toFixed(4)} zł/kWh
                    </Text>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/**
 * Memoized export - prevents re-renders when parent re-renders
 * but props haven't changed (shallow comparison)
 */
export const ChartsVisualization = memo(ChartsVisualizationInner);
